import { friendshipStatusQueryOptions } from '@/api/queries/friendship';
import { useQuery } from '@tanstack/react-query';

interface UseFriendshipStatusOptions {
  /**
   * Whether the query should be enabled
   * Set to false to prevent the query from running (e.g., when viewing own profile)
   * @default true
   */
  enabled?: boolean;
}

/**
 * Hook to check friendship status with a user
 * Uses domain-oriented query options
 *
 * @param userId - The user ID to check friendship status with
 * @param options - Optional query options
 * @returns Query result with status and requestId
 *
 * @example
 * ```typescript
 * const { data, isLoading } = useFriendshipStatus('user123');
 * // data.status: 'none' | 'pending_sent' | 'pending_received' | 'friends'
 * // data.requestId?: string (if pending)
 *
 * // Disable query when viewing own profile
 * const { data } = useFriendshipStatus(userId, { enabled: !isCurrentUser });
 * ```
 */
export function useFriendshipStatus(userId: string, options?: UseFriendshipStatusOptions) {
  return useQuery({
    ...friendshipStatusQueryOptions(userId),
    enabled: options?.enabled !== false && !!userId,
  });
}
